import { Word } from '@/player-config/src/types/transcript-types';
import { TranscriptParagraph, SearchParagraph, SearchResult } from '@/components/molecules/Transcript/types';
import { addDecimalIfNeeded } from '@/libs/utils';
import { convertParagraphToSearchParagraph, getParagraphSearchResults } from '@/components/molecules/Transcript/utils';
import {
  getAllEditingWordOccurrences,
  getMergesTimeRanges
} from '@/components/molecules/Transcript/TranscriptInlineEdit/utils';
import { EditedWord } from '@/player-config/src/types/clip-types';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';

/**
 * Applies a word edit to the clip data. Handles both single word edits and multi-word merges.
 * For single word: updates the edits and merges with the new value
 * For multiple words: creates a merge entry spanning all selected words
 */
export function applyWordEdit(compositeSegment: CompositeSegment, words: Word[], newValue: string): CompositeSegment {
  if (words.length === 0) {
    return compositeSegment;
  }

  if (words.length > 1) {
    return applyWordMerge(compositeSegment, words, newValue);
  }

  const word = words[0];
  const editedWord: EditedWord = {
    start_time: word.start_time,
    end_time: word.end_time,
    word: newValue
  };

  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }

  if (!compositeSegment.segment_metadata.edits) {
    compositeSegment.segment_metadata.edits = {};
  }

  const currentWordEdit = compositeSegment.segment_metadata.edits[addDecimalIfNeeded(word.start_time)];
  const isHiddenWord = currentWordEdit && !currentWordEdit.word;

  compositeSegment.segment_metadata.edits = {
    ...compositeSegment.segment_metadata.edits,
    ...(isHiddenWord ? {} : { [addDecimalIfNeeded(word.start_time)]: editedWord })
  };
  compositeSegment.segment_metadata.merges = (compositeSegment.segment_metadata.merges ?? [])
    .filter(({ start_time }) => start_time !== editedWord.start_time)
    .concat(editedWord);

  return compositeSegment;
}

/**
 * Merges multiple words into a single word with the given value.
 * Creates a merge entry that spans from the first word's start to the last word's end.
 */
function applyWordMerge(compositeSegment: CompositeSegment, words: Word[], newValue: string): CompositeSegment {
  const startTime = words[0].start_time;
  const endTime = words[words.length - 1].end_time;

  const editedWord: EditedWord = {
    start_time: startTime,
    end_time: endTime,
    word: newValue
  };

  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }

  if (!compositeSegment.segment_metadata.edits) {
    compositeSegment.segment_metadata.edits = {};
  }

  compositeSegment.segment_metadata.edits = {
    ...compositeSegment.segment_metadata.edits,
    [addDecimalIfNeeded(editedWord.start_time)]: editedWord
  };
  compositeSegment.segment_metadata.merges = (compositeSegment.segment_metadata.merges ?? [])
    .filter(({ start_time }) => start_time !== editedWord.start_time)
    .filter(({ start_time }) => !(start_time >= editedWord.start_time && start_time <= editedWord.end_time))
    .concat(editedWord);

  return compositeSegment;
}

/**
 * Hides the given words by setting their word value to empty string in edits.
 */
export function applyWordHide(compositeSegment: CompositeSegment, words: Word[]): CompositeSegment {
  if (words.length === 0) {
    return compositeSegment;
  }

  words.forEach(word => {
    const editedWord: EditedWord = {
      start_time: word.start_time,
      end_time: word.end_time,
      word: ''
    };

    if (!compositeSegment.segment_metadata) {
      throw new Error('Composite segment metadata not found');
    }
    if (!compositeSegment.segment_metadata.edits) {
      compositeSegment.segment_metadata.edits = {};
    }
    compositeSegment.segment_metadata.edits = {
      ...compositeSegment.segment_metadata.edits,
      [addDecimalIfNeeded(word.start_time)]: editedWord
    };
  });

  return compositeSegment;
}

/**
 * Shows the given words by removing their entries from edits.
 */
export function applyWordShow(compositeSegment: CompositeSegment, words: Word[]): CompositeSegment {
  if (words.length === 0) {
    return compositeSegment;
  }

  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }

  if (!compositeSegment.segment_metadata.edits) {
    compositeSegment.segment_metadata.edits = {};
  }

  const edits = compositeSegment.segment_metadata.edits;
  words.forEach(({ start_time }) => {
    delete edits[addDecimalIfNeeded(start_time)];
  });
  return compositeSegment;
}

/**
 * Corrects all occurrences of the editing content in the transcript.
 * Handles both single word corrections and multi-word merge corrections.
 */
export function applyCorrectAll(
  compositeSegment: CompositeSegment,
  paragraphs: TranscriptParagraph[],
  editingContent: string,
  newValue: string,
  selectedWords: Word[]
): CompositeSegment {
  const content = paragraphs.map((paragraph, index) =>
    convertParagraphToSearchParagraph(paragraph.words, index, paragraph.segmentId)
  );
  const foundParagraphs: SearchResult[] = content.reduce((acc: SearchResult[], el: SearchParagraph) => {
    return [...acc, ...getParagraphSearchResults(el, editingContent)];
  }, []);

  if (!compositeSegment.segment_metadata) {
    throw new Error('Composite segment metadata not found');
  }

  if (selectedWords.length > 1) {
    const ranges = getMergesTimeRanges(foundParagraphs);
    ranges.forEach(r => {
      const startTime = r.startTime;
      const endTime = r.endTime;

      const editedWord: EditedWord = {
        start_time: startTime,
        end_time: endTime,
        word: newValue
      };
      if (!compositeSegment.segment_metadata) {
        throw new Error('Composite segment metadata not found');
      }
      if (!compositeSegment.segment_metadata.edits) {
        compositeSegment.segment_metadata.edits = {};
      }
      compositeSegment.segment_metadata.merges = (compositeSegment.segment_metadata.merges ?? [])
        .filter(({ start_time }) => start_time !== editedWord.start_time)
        .filter(({ start_time }) => !(start_time >= editedWord.start_time && start_time <= editedWord.end_time))
        .concat(editedWord);
      compositeSegment.segment_metadata.edits = {
        ...compositeSegment.segment_metadata.edits,
        [addDecimalIfNeeded(editedWord.start_time)]: editedWord
      };
    });
  } else {
    const editingWordOccurrences = getAllEditingWordOccurrences(foundParagraphs, editingContent);
    const regex = new RegExp(editingContent, 'gi');

    editingWordOccurrences.forEach(w => {
      const replacedContent = w.content.replace(regex, newValue);
      applyWordEdit(compositeSegment, [w as unknown as Word], replacedContent);
    });
  }

  return compositeSegment;
}
